import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";

import { PollResultsGroup, sumCounts } from "./pollResultsShared";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 620,
  maxWidth: "94vw",
  maxHeight: "85vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflowY: "auto",
  border: "none",
};

/**
 * Read-only, deck-level summary of every poll card's results in one place — the
 * header entry point next to Share. Results stay per-card (anonymous aggregates,
 * not per-respondent records), so this lists each poll card with its public +
 * assigned bars rather than imitating the form's per-respondent Responses table.
 *
 * Reads the `pollResults` map already loaded with the deck detail
 * ({ cardUuid: { public: {idx:n}, assigned: {idx:n} } }) and reuses
 * PollResultsGroup so it matches the inline card editor exactly.
 */
// `hideAssigned`: Engagement decks are never assigned as training, so only the
// public-link tally is shown.
function SwipeDeckPollResultsModal({ open, onClose, deckName, cards, pollResults, hideAssigned = false }) {
  const pollCards = (cards || []).filter((c) => c.card_type === "poll");

  return (
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <Box sx={style}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 rounded-t-2xl flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Poll results</h3>
            <p className="text-sm text-slate-600">
              {deckName ? `${deckName} · ` : ""}anonymous aggregates
            </p>
          </div>
          <button
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="px-6 py-4">
          {pollCards.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">
              This deck has no poll cards.
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {pollCards.map((card, idx) => {
                const content = card.content_json || {};
                const choices = content.choices || [];
                const result = (pollResults && pollResults[card.uuid]) || {};
                const publicCounts = result.public || {};
                const assignedCounts = hideAssigned ? {} : result.assigned || {};
                const publicTotal = sumCounts(publicCounts);
                const assignedTotal = sumCounts(assignedCounts);
                const hasVotes = publicTotal > 0 || assignedTotal > 0;

                return (
                  <div
                    key={card.uuid || idx}
                    className="rounded-lg border border-slate-200 p-3"
                  >
                    <div className="text-sm font-semibold text-slate-900">
                      {content.question || `Poll ${idx + 1}`}
                    </div>
                    {!hasVotes ? (
                      <div className="mt-2 text-xs text-slate-400">
                        No responses yet.
                      </div>
                    ) : (
                      <>
                        {publicTotal > 0 && (
                          <PollResultsGroup
                            title={`Public link · ${publicTotal} ${
                              publicTotal === 1 ? "vote" : "votes"
                            }`}
                            counts={publicCounts}
                            total={publicTotal}
                            choices={choices}
                          />
                        )}
                        {assignedTotal > 0 && (
                          <PollResultsGroup
                            title={`Assigned training · ${assignedTotal} ${
                              assignedTotal === 1 ? "vote" : "votes"
                            }`}
                            counts={assignedCounts}
                            total={assignedTotal}
                            choices={choices}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
}

export default SwipeDeckPollResultsModal;
